// Dependencies
import { all, call, takeEvery, fork, put } from "@redux-saga/core/effects";
// Action Types
import {
    TAGS_SAVE_TAG,
    TAGS_GET_TAG,
    TAGS_GET_ENTITIES
} from "Redux/actionTypes";

// Actions
import {
    tagsSaveTagResponse,
    tagsGetTagResponse,
    tagsGetEntitiesResponse
} from "Redux/actions";

// Helpers
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";

function* tagsSaveTagRequest({ payload }) {
    try {
        const { tag } = payload;

        let query = `
            mutation createTag (
                $label: String!, 
                $color: String!, 
                $unitIds: [Int], 
                $companyIds: [Int]
            ) {
                tag: createTag (
                    label: $label, 
                    color: $color,
                    unitIds: $unitIds, 
                    companyIds: $companyIds
                ) {
                    id
                    label
                    color
                }
            }
        `;

        if (tag?.id) {
            query = `
                mutation updateTag (
                    $id: Int!, 
                    $label: String, 
                    $color: String,
                    $unitIds: [Int], 
                    $companyIds: [Int]
                ) {
                    tag: updateTag (
                        id: $id, 
                        label: $label, 
                        color: $color,
                        unitIds: $unitIds, 
                        companyIds: $companyIds
                    ) {
                        id
                        label
                        color
                    }
                }
            `;
        }

        const response = yield call(
            clientMutation,
            query,
            tag,
            endpoints.GRAPHQL_GENERAL,
        );

        if (!response || !response.tag) throw 'Error while creating/updating tag';

        yield put(tagsSaveTagResponse({ ...response.tag, error: false }));
    } catch (exc) {
        yield put(tagsSaveTagResponse({ error: true }));
        ExceptionManager(
            exc,
            "modules/account/views/tags/components/redux/sagas",
            "tagsSaveTagRequest",
            true
        );
    }
}

function* tagsGetTagRequest({ payload }) {
    try {
        const { id } = payload;
        const query = `
            query getTag ($id: Int!) {
                tag: getTag (id: $id) {
                    id
                    label
                    color
                    parent {
                        id
                        label
                        color
                    }
                }
            }
        `;

        const response = yield call(
            clientQuery,
            query,
            {
                id
            },
            endpoints.GRAPHQL_GENERAL
        );

        if (!response?.tag) throw `Error while getting tag ${id}`;

        yield put(tagsGetTagResponse({ ...response?.tag, error: false }));
    } catch (exc) {
        yield put(tagsGetTagResponse({ error: true }));
        ExceptionManager(
            exc,
            "modules/account/views/tags/components/redux/sagas",
            "tagsGetTagRequest"
        );
    }
}

function* tagsGetEntitiesRequest({ payload }) {
    try {
        
        const { data } = payload;
        const query = `
            query searchTagsAndEntities (
                $parentTagIds: [Int], 
                $entityName: String!, 
                $openSearch: String,
                $onlyEntities: Boolean,
                $limit: Int, 
                $offset: Int
            ) {
                entityTags: searchTagsAndEntities(
                    parentTagIds: $parentTagIds, 
                    entityName: $entityName, 
                    limit: $limit, 
                    offset: $offset,
                    openSearch: $openSearch,
                    onlyEntities: $onlyEntities,
                ) {
                    items {
                        name
                        id
                        label
                    }
                    total
                }
            }
        `;

        const response = yield call(
            clientQuery,
            query,
            {
                parentTagIds: data?.parentTagId ? [data?.parentTagId] : [],
                openSearch: "",
                entityName: data?.entityName,
                limit: 0,
                offset: 0,
                onlyEntities: true
            },
            endpoints.GRAPHQL_GENERAL
        );

        if (!response?.entityTags) throw `Error while getting entityTags ${data?.parentTagId}`;
        
        const { entityTags: { items, total }} = response;
        yield put(tagsGetEntitiesResponse({ items, total, entityName: data?.entityName, error: false }));
    } catch (exc) {
        yield put(tagsGetEntitiesResponse({ items: [], error: true }));
        ExceptionManager(
            exc,
            "modules/account/views/tags/components/redux/sagas",
            "tagsGetEntitiesRequest"
        );
    }
}

function* watchMethods() {
    yield takeEvery(TAGS_SAVE_TAG, tagsSaveTagRequest);
    yield takeEvery(TAGS_GET_TAG, tagsGetTagRequest);
    yield takeEvery(TAGS_GET_ENTITIES, tagsGetEntitiesRequest);
}

export default function* rootSaga() {
    yield all([fork(watchMethods)]);
}